/**
 * La composizione grafica OI che apre la notizia finché non c'è una foto
 * da mettere al suo posto (vedi `immagineNotizia` in lib/notizia.ts).
 *
 * È la tavola di uploads/Consegna_Notizia_OI.zip (Notizia_proposta_OI.html,
 * `figure.abstract-art`), ridisegnata uguale: a sinistra il segno «OI»,
 * «Osservatorio Italiano» in serif e il motto sopra un filetto sabbia; a
 * destra tre fogli impilati, un riquadro di dati e i circuiti. Il segno
 * è lo stesso tracciato di <LogoOI variant="segno">.
 *
 * Il viewBox è quello del riferimento, 720×320, e il riquadro che la
 * contiene ha lo stesso rapporto (9:4): la tavola si
 * adatta alla larghezza della colonna senza deformarsi e senza ritagli.
 *
 * Una differenza dal riferimento, voluta:
 *
 * - i colori non sono scritti qui: sono i token `--oi-cn-*` di
 *   app/globals.css, coi valori del riferimento. La tavola è scura nei
 *   due temi, come nel riferimento.
 *
 * Nessuno stato e nessun effetto: si rende anche dal server.
 *
 * Dal 06/10/2026 fa da ripiego anche nelle schede della prima pagina
 * («Segnali da seguire», i dossier) quando il dispaccio non ha foto. Lì
 * compare più volte per pagina, quindi ogni copia porta un `idGradiente`
 * suo, ed è `decorativa`: la scheda è già un collegamento col suo titolo,
 * e il nome della tavola ripetuto quattro volte sarebbe solo rumore per
 * un lettore di schermo.
 */
export function CompostaOI({
  className,
  idGradiente = "oi-cn-foglio",
  decorativa = false,
}: {
  className?: string;
  idGradiente?: string;
  decorativa?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 720 320"
      {...(decorativa
        ? { "aria-hidden": true }
        : {
            role: "img",
            "aria-label":
              "Osservatorio Italiano: documenti, informazione e relazioni digitali",
          })}
      className={className}
    >
      <defs>
        {/* Un id scritto da chi chiama e non da `useId`, che porterebbe
            dentro `url(#…)` caratteri che il riferimento CSS non digerisce.
            Sulla pagina della notizia la tavola è una sola e basta il
            valore di serie; nelle schede ognuna ha il suo. */}
        <linearGradient id={idGradiente} x1="0" y1="0" x2="1" y2="1">
          <stop style={{ stopColor: "var(--oi-cn-foglio-a)" }} />
          <stop offset="1" style={{ stopColor: "var(--oi-cn-foglio-b)" }} />
        </linearGradient>
      </defs>
      <rect width="720" height="320" style={{ fill: "var(--oi-cn-fondo)" }} />
      <rect x="355" width="365" height="320" style={{ fill: "var(--oi-cn-fondo-destra)" }} />
      <g fill="none" opacity=".6" style={{ stroke: "var(--oi-cn-circuito)" }}>
        <path d="M370 63H398V122H420M370 255H403V211H445M611 65H668V148H638M618 244H680V198H641" />
        <rect x="363" y="56" width="10" height="10" />
        <rect x="363" y="250" width="10" height="10" />
        <rect x="675" y="191" width="10" height="10" />
      </g>
      <rect
        x="465" y="34" width="176" height="225" rx="3"
        style={{ fill: "var(--oi-cn-foglio-retro)", stroke: "var(--oi-cn-foglio-bordo)" }}
      />
      <rect x="442" y="49" width="176" height="225" rx="3" style={{ fill: "var(--oi-cn-foglio-mezzo)" }} />
      <rect x="418" y="65" width="176" height="225" rx="3" fill={`url(#${idGradiente})`} />
      <g style={{ fill: "var(--oi-cn-righe)" }}>
        <rect x="437" y="85" width="40" height="6" />
        <rect x="482" y="85" width="20" height="6" opacity=".35" />
        <rect x="437" y="109" width="132" height="5" />
        <rect x="437" y="120" width="107" height="5" />
        <rect x="437" y="143" width="62" height="49" opacity=".15" />
        <rect x="510" y="143" width="59" height="4" />
        <rect x="510" y="154" width="47" height="4" />
        <rect x="510" y="165" width="59" height="4" />
        <rect x="510" y="176" width="35" height="4" />
        <rect x="437" y="209" width="132" height="4" />
        <rect x="437" y="220" width="119" height="4" />
        <rect x="437" y="231" width="132" height="4" />
        <rect x="437" y="242" width="85" height="4" />
      </g>
      <rect
        x="536" y="182" width="151" height="70"
        style={{ fill: "var(--oi-cn-dati)", stroke: "var(--oi-cn-sabbia)" }}
      />
      <g style={{ fill: "var(--oi-cn-sabbia)" }}>
        <rect x="551" y="197" width="7" height="7" />
        <rect x="562" y="197" width="7" height="7" />
        <rect x="584" y="197" width="7" height="7" />
        <rect x="606" y="197" width="7" height="7" />
        <rect x="617" y="197" width="7" height="7" />
        <rect x="650" y="197" width="7" height="7" />
        <rect x="551" y="211" width="7" height="7" />
        <rect x="573" y="211" width="7" height="7" />
        <rect x="595" y="211" width="7" height="7" />
        <rect x="628" y="211" width="7" height="7" />
        <rect x="650" y="211" width="7" height="7" />
      </g>
      <path d="M551 235H671" style={{ stroke: "var(--oi-cn-circuito-scuro)" }} />
      <g transform="translate(38 43) scale(.18)" style={{ fill: "var(--oi-cn-segno)" }}>
        <path
          fillRule="evenodd"
          d="M61 0H276L337 61V294L276 355H61L0 294V61ZM77 93V262L97 282H240L260 262V93L240 73H97Z"
        />
        <path d="M303 0H445V136Z M357 75L445 159V355H357Z" />
      </g>
      <text
        x="38" y="167" fontSize="34"
        style={{ fill: "var(--oi-cn-testo)", fontFamily: "var(--font-serif), Georgia, serif" }}
      >
        Osservatorio
      </text>
      <text
        x="38" y="208" fontSize="34"
        style={{ fill: "var(--oi-cn-testo)", fontFamily: "var(--font-serif), Georgia, serif" }}
      >
        Italiano
      </text>
      <path d="M38 241H300" style={{ stroke: "var(--oi-cn-sabbia-testo)" }} />
      <text
        x="38" y="270" fontSize="14" letterSpacing=".3"
        style={{ fill: "var(--oi-cn-sabbia-testo)", fontFamily: "var(--font-sans), Arial, sans-serif" }}
      >
        La cibernetica dell’informazione
      </text>
    </svg>
  );
}

/** Il fondo del riquadro che ospita la tavola quando il riquadro non è
 *  9:4 (le schede della prima pagina sono alte 150 a qualunque larghezza).
 *  La tavola resta intera (`meet`) e le fasce che avanzano sopra e sotto
 *  continuano i suoi due fondi: il taglio fra le due metà sta a 355 su
 *  720, cioè al 49,3%, e con la tavola adattata in larghezza cade nello
 *  stesso punto. `slice` invece avrebbe tagliato la scritta a sinistra
 *  nelle schede strette (225 pixel a 1024). */
export const FONDO_COMPOSTA_OI =
  "linear-gradient(to right, var(--oi-cn-fondo) 49.3%, var(--oi-cn-fondo-destra) 49.3%)";
